import getVWKey from "../helpers/getVWKey"
import Utils from "../utils";
import * as Events from "../utils/events";
import AssetLibrary from "../assets/asset-library";
import Base2DElement from "./base-2d-element";

class AtomUI extends Base2DElement {
    constructor(options, parentLayer, uuid = THREE.Math.generateUUID()) {
        super(options, parentLayer, uuid)

        this.atom = options.atom;

        this._createInfoTextbox();
        this._createPeriodicTable();
        this._createInfoLabels();
        this._createControls();

        this._needsUpdate = {
            visible: true,
            infoBoxVisible: true,
            infoLabelsVisible: true,
            controlsVisible: true,
            tableVisible: true,
            tableInteractive: true,
            particleCount: true,
        };

        this._eventsUpdate = [
            { event: 'atom_toggle_visibility', updateProperty: 'visible' },
            { event: 'atom_toggle_controls', updateProperty: 'controlsVisible' },
            { event: 'atom_toggle_info_labels', updateProperty: 'infoLabelsVisible' },
            { event: 'atom_toggle_info_box', updateProperty: 'infoBoxVisible' },
            { event: 'atom_toggle_periodic_table', updateProperty: 'tableVisible' },
            { event: 'atom_toggle_interactive_table', updateProperty: 'tableInteractive' },
            { event: 'atom_particle_count_changed', updateProperty: 'particleCount' },
        ];

        for (let eventInfo of this._eventsUpdate) {
            eventInfo.updateFunction = (atom) => {
                if (atom.uuid === this.atom) {
                    this._needsUpdate[eventInfo.updateProperty] = true;
                }
            };

            Events.listen(eventInfo.event, eventInfo.updateFunction);
        }

    }

    _initValues(options) {
        this._type = "2DObject";
        this._type2d = "AtomUI";
        this._index = options.index;
    }

    _createContainer() {
        const container = super._createContainer();

        container.className = 'atom-cnt';

        return container;
    }

    _getAtomData() {
        return Q3.data.assets.objects[this.atom];
    }

    _getAtomObject() {
        return AssetLibrary.objects[this.atom];
    }

    setAtom(atom) {
        this.atom = atom;

        for (let property in this._needsUpdate) {
            this._needsUpdate[property] = true;
        }
    }

    redraw(width, height) {
        let update = this._needsUpdate;

        if (update.visible) {
            this._updateAllVisibility();
            update.visible = false;
        }

        if (update.infoBoxVisible || update.infoLabelsVisible || update.controlsVisible || update.tableVisible) {
            this._updateUiVisibility();
            update.infoBoxVisible = false;
            update.infoLabelsVisible = false;
            update.controlsVisible = false;
            update.tableVisible = false;
        }

        if (update.tableInteractive) {
            this._updateTableInteractive();
            update.tableInteractive = false;
        }

        if (update.particleCount) {
            this._updateDisplay();
            update.particleCount = false;
        }
    }

    render(width, height) {
        if (!Q3.data.layers.layers2D[this.parentLayer.uuid] || this.disabled || Utils.queryParams.has("disable_2d")) return;

        if (!this.isMounted) {
            for (let property in this._needsUpdate) {
                this._needsUpdate[property] = true;
            }

            if (width > 0 && height > 0) {
                this.isMounted = true;
            }
        }

        this.redraw(width, height);
    }

    delete() {

        for (let eventInfo of this._eventsUpdate) {
            Events.unlisten(eventInfo.event, eventInfo.updateFunction);
        }

        this.parentLayer.objects.splice(this.parentLayer.objects.indexOf(this), 1);
        // this.parentLayer.domElement.removeChild(this.domElement);

        delete this.domElement;

        if (Q3.selected === this) {
            Q3.selected = null;
            Events.fire('selectchange');
        }

    }

    _updateDisplay() {
        let atom = this._getAtomObject();
        if (!atom) return;

        this._updateInfoTextbox();
        this._periodicTable.setHighlighted(atom.protons);
        this._updateInfoLabels();
        this._updateControlsDisplay();

        Events.fire("atom_ui_updated");
    }

    _createInfoTextbox() {
        const container = document.createElement("div");
        container.classList.add("atom-info");

        const top = document.createElement("div");
        container.appendChild(top);

        const atomicMass = document.createElement("div");
        atomicMass.classList.add("atomic-mass");
        top.appendChild(atomicMass);
        atomicMass.innerText = 6;

        const atomicNumber = document.createElement("div");
        atomicNumber.classList.add("atomic-number");
        top.appendChild(atomicNumber);
        atomicNumber.innerText = 6;

        const middle = document.createElement("div");
        container.appendChild(middle);

        const chemicalSymbol = document.createElement("div");
        chemicalSymbol.classList.add("chemical-symbol")
        middle.appendChild(chemicalSymbol);
        chemicalSymbol.innerText = "C";

        const bottom = document.createElement("div");
        container.appendChild(bottom);

        const chemicalName = document.createElement("div");
        chemicalName.classList.add("chemical-name");
        bottom.appendChild(chemicalName);
        chemicalName.innerText = "Carbon";

        this._infoTextbox = Object.freeze({
            container,
            atomicMass,
            atomicNumber,
            chemicalName,
            chemicalSymbol
        });

        this._domElement.appendChild(container);
    }

    _updateInfoTextbox() {
        let atom = this._getAtomObject();
        if (!atom) return;
        const { protons, neutrons, electrons } = atom;
        const element = elements[protons] || new AtomInfo();

        const mass = protons * massess.proton + neutrons * massess.neutron + electrons * massess.electron;

        this._infoTextbox.atomicNumber.innerText = protons;
        this._infoTextbox.chemicalName.innerText = element.name;
        this._infoTextbox.chemicalSymbol.innerText = element.symbol;
        this._infoTextbox.atomicMass.innerText = mass.toFixed(5);
        this._infoTextbox.container.style.backgroundColor = GROUPS[element.type].color;
    }

    _createPeriodicTable() {
        let table = new PeriodicTable((p, n, e) => {
            let atom = this._getAtomObject();
            if (!atom) return;

            if (atom.interactiveTable) {
                atom.setParticleCount(p, n, e);
            }
        });
        this._periodicTable = table;
        this._domElement.appendChild(table.domElement);
    }

    _createInfoLabels() {
        const container = document.createElement("div");
        const nameCnt = document.createElement("div");
        const valueCnt = document.createElement("div");
        container.classList.add("atom-info-labels");
        nameCnt.classList.add("names");
        valueCnt.classList.add("values");

        container.appendChild(nameCnt);
        container.appendChild(valueCnt);

        let unknownElement = new AtomInfo();
        let getElement = (p) => elements[p] || unknownElement;

        let getCharge = (p, n, e) => {
            let c = p - e;
            if (c > 0) c = '+' + c;
            return c;
        };

        let getGroup0 = (i) => i * 18;
        let getGroup1 = (i) => (i < 2 ? i : 10 + i) + 1;
        let getGroup2 = (i) => i + 1;
        let getGroup3 = (i) => i < 3 ? i + 1 : i < 17 ? 'n/a' : i - 13;

        let periodElements = [2, 8, 8, 18, 18, 32, 32];
        let periodGroups = [getGroup0, getGroup1, getGroup1, getGroup2, getGroup2, getGroup3, getGroup3];
        let elementPeriods = [], elementGroups = [];
        for (let period = 0; period < periodElements.length; period++) {
            let elementCount = periodElements[period];
            for (let i = 0; i < elementCount; i++) {
                elementPeriods.push(period + 1);
                elementGroups.push(periodGroups[period](i));
            }
        }
        let getPeriod = (p, n, e) => {
            let period = elementPeriods[p - 1];
            return period !== undefined ? period : "?";
        };
        let getGroup = (p, n, e) => {
            let group = elementGroups[p - 1];
            return group !== undefined ? group : "?";
        };

        let properties = [
            { name: "Atomic number", value: (p, n, e) => p },
            { name: "Symbol", value: (p, n, e) => getElement(p).symbol },
            { name: "Rel. atomic mass", value: (p, n, e) => p + n },
            { name: "Element name", value: (p, n, e) => getElement(p).name },
            { name: "Charge", value: getCharge },
            { name: "Group", value: getGroup },
            { name: "Period", value: getPeriod },
        ];

        this._infoLabelRoot = container;
        this._infoLabels = [];

        for (let property of properties) {
            const nameLabel = document.createElement("div");
            const valueLabel = document.createElement("div");

            nameLabel.innerText = property.name + ':';
            this._infoLabels.push({ domElement: valueLabel, value: property.value });

            nameCnt.appendChild(nameLabel);
            valueCnt.appendChild(valueLabel);
        }

        this._domElement.appendChild(container);
    }

    _updateInfoLabels() {
        let atom = this._getAtomObject();
        if (!atom) return;

        const [p, n, e] = [atom.protons, atom.neutrons, atom.electrons];

        for (let label of this._infoLabels) {
            label.domElement.innerText = label.value(p, n, e);
        }
    }

    _createControls() {
        const container = document.createElement("div");
        container.classList.add("atom-controls");

        let controls = [
            {
                name: "Electrons",
                count: "electrons",
                addFunction: () => {
                    let atom = this._getAtomObject();
                    atom.addElectron();
                },
                removeFunction: () => {
                    let atom = this._getAtomObject();
                    atom.removeElectron();
                },
            }, {
                name: "Protons",
                count: "protons",
                addFunction: () => {
                    let atom = this._getAtomObject();
                    atom.addProton();
                },
                removeFunction: () => {
                    let atom = this._getAtomObject();
                    atom.removeProton();
                },
            }, {
                name: "Neutrons",
                count: "neutrons",
                addFunction: () => {
                    let atom = this._getAtomObject();
                    atom.addNeutron();
                },
                removeFunction: () => {
                    let atom = this._getAtomObject();
                    atom.removeNeutron();
                },
            }
        ];

        this._controls = {
            container: container,
            countLabels: []
        };

        for (let control of controls) {
            let controlCnt = document.createElement("div");
            controlCnt.classList.add("control");

            let nameLabel = document.createElement("div");
            let countCnt = document.createElement("div");
            countCnt.classList.add("count");

            let countLabel = document.createElement("div");
            nameLabel.classList.add("name");
            countLabel.classList.add("count-label");

            let btnCnt = document.createElement("div");
            btnCnt.classList.add("count-buttons");
            let addBtn = document.createElement("div");
            let removeBtn = document.createElement("div");
            addBtn.innerHTML = '&#x21e7;';
            removeBtn.innerHTML = '&#x21e9;';
            addBtn.classList.add("button");
            removeBtn.classList.add("button");

            nameLabel.innerText = control.name;

            countCnt.appendChild(btnCnt);
            countCnt.appendChild(countLabel);
            btnCnt.appendChild(addBtn);
            btnCnt.appendChild(removeBtn);
            controlCnt.appendChild(countCnt);
            controlCnt.appendChild(nameLabel);

            this._controls.countLabels.push({ count: control.count, label: countLabel });

            addBtn.addEventListener("click", control.addFunction);
            removeBtn.addEventListener("click", control.removeFunction);

            container.appendChild(controlCnt);
        }

        this._domElement.appendChild(container);
    }

    _updateControlsDisplay() {
        let atom = this._getAtomObject();
        if (!atom) return;

        for (let countLabel of this._controls.countLabels) {
            countLabel.label.innerText = atom[countLabel.count];
        }
    }

    _updateAllVisibility() {
        let atom = this._getAtomObject();
        let show = atom && atom.visible;
        this._domElement.classList.toggle("hidden", !show);

        Events.fire("atom_ui_updated");
    }

    _updateUiVisibility() {
        let atom = this._getAtomObject();
        if (!atom) return;

        this._infoTextbox.container.classList.toggle("hidden", !atom.infoBoxVisible);
        this._infoLabelRoot.classList.toggle("hidden", !atom.infoLabelsVisible);
        this._controls.container.classList.toggle("hidden", !atom.controlsVisible);
        this._periodicTable.setVisible(atom.enablePeriodicTable);

        Events.fire("atom_ui_updated");
    }

    _updateTableInteractive() {
        let atom = this._getAtomObject();
        if (!atom) return;

        this._periodicTable.setInteractive(atom.interactiveTable);

        Events.fire("atom_ui_updated");
    }
}

const GROUPS = Object.freeze({
    alkaliMetals: {
        name: "Alkali metals",
        color: "#ffc591"
    },
    alkalineMetals: {
        name: "Alkaline metals",
        color: "#ffdf91"
    },
    otherMetals: {
        name: "Other metals",
        color: "#fff991"
    },
    transitionMetals: {
        name: "Transition metals",
        color: "#ecff91"
    },
    lanthanoids: {
        name: "Lanthanoids",
        color: "#d2ff91"
    },
    actinoids: {
        name: "Actinoids",
        color: "#b8ff91"
    },
    metalloids: {
        name: "Metalloids",
        color: "#91ff9e"
    },
    nonmetals: {
        name: "Nonmetals",
        color: "#ab91ff"
    },
    halogens: {
        name: "Halogens",
        color: "#f991ff"
    },
    nobleGases: {
        name: "Noble gases",
        color: "#91dfff"
    },
    unknown: {
        name: "Unknown",
        color: "#c8c8c8"
    }
});

const massess = Object.freeze({
    proton: 1.007276466879,
    neutron: 1.00866491588,
    electron: 5.48579909070e-4
});

/**
 * @type {AtomInfo[]}
 */
const elements = new Array(119);

class AtomInfo {
    constructor({ name = "Unknown", symbol = "?", type = "unknown", atomicNumber = 0, isRadioactive = false } = {}) {
        this.name = name;
        this.symbol = symbol;
        this.type = type;
        this.atomicNumber = atomicNumber;
        this.isRadioactive = isRadioactive;
    }
}

[
    new AtomInfo({ name: "Hydrogen", symbol: "H", type: "nonmetals", atomicNumber: 1, isRadioactive: false }),
    new AtomInfo({ name: "Helium", symbol: "He", type: "nobleGases", atomicNumber: 2, isRadioactive: false }),

    new AtomInfo({ name: "Lithium", symbol: "Li", type: "alkaliMetals", atomicNumber: 3, isRadioactive: false }),
    new AtomInfo({ name: "Berylium", symbol: "Be", type: "alkalineMetals", atomicNumber: 4, isRadioactive: false }),
    new AtomInfo({ name: "Boron", symbol: "B", type: "metalloids", atomicNumber: 5, isRadioactive: false }),
    new AtomInfo({ name: "Carbon", symbol: "C", type: "nonmetals", atomicNumber: 6, isRadioactive: false }),
    new AtomInfo({ name: "Nitrogen", symbol: "N", type: "nonmetals", atomicNumber: 7, isRadioactive: false }),
    new AtomInfo({ name: "Oxygen", symbol: "O", type: "nonmetals", atomicNumber: 8, isRadioactive: false }),
    new AtomInfo({ name: "Fluorine", symbol: "F", type: "halogens", atomicNumber: 9, isRadioactive: false }),
    new AtomInfo({ name: "Neon", symbol: "Ne", type: "nobleGases", atomicNumber: 10, isRadioactive: false }),

    new AtomInfo({ name: "Sodium", symbol: "Na", type: "alkaliMetals", atomicNumber: 11, isRadioactive: false }),
    new AtomInfo({ name: "Magnesium", symbol: "Mg", type: "alkalineMetals", atomicNumber: 12, isRadioactive: false }),
    new AtomInfo({ name: "Aluminium", symbol: "Al", type: "otherMetals", atomicNumber: 13, isRadioactive: false }),
    new AtomInfo({ name: "Silicon", symbol: "Si", type: "metalloids", atomicNumber: 14, isRadioactive: false }),
    new AtomInfo({ name: "Phosphorus", symbol: "P", type: "nonmetals", atomicNumber: 15, isRadioactive: false }),
    new AtomInfo({ name: "Sulfur", symbol: "S", type: "nonmetals", atomicNumber: 16, isRadioactive: false }),
    new AtomInfo({ name: "Chlorine", symbol: "Cl", type: "halogens", atomicNumber: 17, isRadioactive: false }),
    new AtomInfo({ name: "Argon", symbol: "Ar", type: "nobleGases", atomicNumber: 18, isRadioactive: false }),

    new AtomInfo({ name: "Potassium", symbol: "K", type: "alkaliMetals", atomicNumber: 19, isRadioactive: false }),
    new AtomInfo({ name: "Calcium", symbol: "Ca", type: "alkalineMetals", atomicNumber: 20, isRadioactive: false }),
    new AtomInfo({ name: "Scandium", symbol: "Sc", type: "transitionMetals", atomicNumber: 21, isRadioactive: false }),
    new AtomInfo({ name: "Titanium", symbol: "Ti", type: "transitionMetals", atomicNumber: 22, isRadioactive: false }),
    new AtomInfo({ name: "Vanadium", symbol: "V", type: "transitionMetals", atomicNumber: 23, isRadioactive: false }),
    new AtomInfo({ name: "Chromium", symbol: "Cr", type: "transitionMetals", atomicNumber: 24, isRadioactive: false }),
    new AtomInfo({ name: "Manganese", symbol: "Mn", type: "transitionMetals", atomicNumber: 25, isRadioactive: false }),
    new AtomInfo({ name: "Iron", symbol: "Fe", type: "transitionMetals", atomicNumber: 26, isRadioactive: false }),
    new AtomInfo({ name: "Cobalt", symbol: "Co", type: "transitionMetals", atomicNumber: 27, isRadioactive: false }),
    new AtomInfo({ name: "Nickel", symbol: "Ni", type: "transitionMetals", atomicNumber: 28, isRadioactive: false }),
    new AtomInfo({ name: "Copper", symbol: "Cu", type: "transitionMetals", atomicNumber: 29, isRadioactive: false }),
    new AtomInfo({ name: "Zinc", symbol: "Zn", type: "otherMetals", atomicNumber: 30, isRadioactive: false }),
    new AtomInfo({ name: "Gallium", symbol: "Ga", type: "otherMetals", atomicNumber: 31, isRadioactive: false }),
    new AtomInfo({ name: "Germanium", symbol: "Ge", type: "metalloids", atomicNumber: 32, isRadioactive: false }),
    new AtomInfo({ name: "Arsenic", symbol: "As", type: "metalloids", atomicNumber: 33, isRadioactive: false }),
    new AtomInfo({ name: "Selenium", symbol: "Se", type: "nonmetals", atomicNumber: 34, isRadioactive: false }),
    new AtomInfo({ name: "Bromine", symbol: "Br", type: "halogens", atomicNumber: 35, isRadioactive: false }),
    new AtomInfo({ name: "Krypton", symbol: "Kr", type: "nobleGases", atomicNumber: 36, isRadioactive: false }),

    new AtomInfo({ name: "Rubidium", symbol: "Rb", type: "alkaliMetals", atomicNumber: 37, isRadioactive: false }),
    new AtomInfo({ name: "Strontium", symbol: "Sr", type: "alkalineMetals", atomicNumber: 38, isRadioactive: false }),
    new AtomInfo({ name: "Yttrium", symbol: "Y", type: "transitionMetals", atomicNumber: 39, isRadioactive: false }),
    new AtomInfo({ name: "Zirconium", symbol: "Zr", type: "transitionMetals", atomicNumber: 40, isRadioactive: false }),
    new AtomInfo({ name: "Niobium", symbol: "Nb", type: "transitionMetals", atomicNumber: 41, isRadioactive: false }),
    new AtomInfo({ name: "Molybdenum", symbol: "Mo", type: "transitionMetals", atomicNumber: 42, isRadioactive: false }),
    new AtomInfo({ name: "Technetium", symbol: "Tc", type: "transitionMetals", atomicNumber: 43, isRadioactive: true }),
    new AtomInfo({ name: "Ruthenium", symbol: "Ru", type: "transitionMetals", atomicNumber: 44, isRadioactive: false }),
    new AtomInfo({ name: "Rhodium", symbol: "Rh", type: "transitionMetals", atomicNumber: 45, isRadioactive: false }),
    new AtomInfo({ name: "Palladium", symbol: "Pd", type: "transitionMetals", atomicNumber: 46, isRadioactive: false }),
    new AtomInfo({ name: "Silver", symbol: "Ag", type: "transitionMetals", atomicNumber: 47, isRadioactive: false }),
    new AtomInfo({ name: "Cadmium", symbol: "Cd", type: "otherMetals", atomicNumber: 48, isRadioactive: false }),
    new AtomInfo({ name: "Indium", symbol: "In", type: "otherMetals", atomicNumber: 49, isRadioactive: false }),
    new AtomInfo({ name: "Tin", symbol: "Sn", type: "otherMetals", atomicNumber: 50, isRadioactive: false }),
    new AtomInfo({ name: "Antimony", symbol: "Sb", type: "metalloids", atomicNumber: 51, isRadioactive: false }),
    new AtomInfo({ name: "Tellurium", symbol: "Te", type: "metalloids", atomicNumber: 52, isRadioactive: false }),
    new AtomInfo({ name: "Iodine", symbol: "I", type: "halogens", atomicNumber: 53, isRadioactive: false }),
    new AtomInfo({ name: "Xenon", symbol: "Xe", type: "nobleGases", atomicNumber: 54, isRadioactive: false }),

    new AtomInfo({ name: "Caesium", symbol: "Cs", type: "alkaliMetals", atomicNumber: 55, isRadioactive: false }),
    new AtomInfo({ name: "Barium", symbol: "Ba", type: "alkalineMetals", atomicNumber: 56, isRadioactive: false }),
    new AtomInfo({ name: "Lanthanum", symbol: "La", type: "lanthanoids", atomicNumber: 57, isRadioactive: false }),
    new AtomInfo({ name: "Cerium", symbol: "Ce", type: "lanthanoids", atomicNumber: 58, isRadioactive: false }),
    new AtomInfo({ name: "Praseodymium", symbol: "Pr", type: "lanthanoids", atomicNumber: 59, isRadioactive: false }),
    new AtomInfo({ name: "Neodymium", symbol: "Nd", type: "lanthanoids", atomicNumber: 60, isRadioactive: false }),
    new AtomInfo({ name: "Promethium", symbol: "Pm", type: "lanthanoids", atomicNumber: 61, isRadioactive: true }),
    new AtomInfo({ name: "Samarium", symbol: "Sm", type: "lanthanoids", atomicNumber: 62, isRadioactive: false }),
    new AtomInfo({ name: "Europium", symbol: "Eu", type: "lanthanoids", atomicNumber: 63, isRadioactive: false }),
    new AtomInfo({ name: "Gadolinium", symbol: "Gd", type: "lanthanoids", atomicNumber: 64, isRadioactive: false }),
    new AtomInfo({ name: "Terbium", symbol: "Tb", type: "lanthanoids", atomicNumber: 65, isRadioactive: false }),
    new AtomInfo({ name: "Dysprosium", symbol: "Dy", type: "lanthanoids", atomicNumber: 66, isRadioactive: false }),
    new AtomInfo({ name: "Holmium", symbol: "Ho", type: "lanthanoids", atomicNumber: 67, isRadioactive: false }),
    new AtomInfo({ name: "Erbium", symbol: "Er", type: "lanthanoids", atomicNumber: 68, isRadioactive: false }),
    new AtomInfo({ name: "Thulium", symbol: "Tm", type: "lanthanoids", atomicNumber: 69, isRadioactive: false }),
    new AtomInfo({ name: "Ytterbium", symbol: "Yb", type: "lanthanoids", atomicNumber: 70, isRadioactive: false }),
    new AtomInfo({ name: "Lutetium", symbol: "Lu", type: "lanthanoids", atomicNumber: 71, isRadioactive: false }),
    new AtomInfo({ name: "Hafnium", symbol: "Hf", type: "transitionMetals", atomicNumber: 72, isRadioactive: false }),
    new AtomInfo({ name: "Tantalum", symbol: "Ta", type: "transitionMetals", atomicNumber: 73, isRadioactive: false }),
    new AtomInfo({ name: "Tungsten", symbol: "W", type: "transitionMetals", atomicNumber: 74, isRadioactive: false }),
    new AtomInfo({ name: "Rhenium", symbol: "Re", type: "transitionMetals", atomicNumber: 75, isRadioactive: false }),
    new AtomInfo({ name: "Osmium", symbol: "Os", type: "transitionMetals", atomicNumber: 76, isRadioactive: false }),
    new AtomInfo({ name: "Iridium", symbol: "Ir", type: "transitionMetals", atomicNumber: 77, isRadioactive: false }),
    new AtomInfo({ name: "Platinum", symbol: "Pt", type: "transitionMetals", atomicNumber: 78, isRadioactive: false }),
    new AtomInfo({ name: "Gold", symbol: "Au", type: "transitionMetals", atomicNumber: 79, isRadioactive: false }),
    new AtomInfo({ name: "Mercury", symbol: "Hg", type: "otherMetals", atomicNumber: 80, isRadioactive: false }),
    new AtomInfo({ name: "Thallium", symbol: "Tl", type: "otherMetals", atomicNumber: 81, isRadioactive: false }),
    new AtomInfo({ name: "Lead", symbol: "Pb", type: "otherMetals", atomicNumber: 82, isRadioactive: false }),
    new AtomInfo({ name: "Bismuth", symbol: "Bi", type: "otherMetals", atomicNumber: 83, isRadioactive: true }),
    new AtomInfo({ name: "Polonium", symbol: "Po", type: "otherMetals", atomicNumber: 84, isRadioactive: true }),
    new AtomInfo({ name: "Astatine", symbol: "At", type: "halogens", atomicNumber: 85, isRadioactive: true }),
    new AtomInfo({ name: "Radon", symbol: "Rn", type: "nobleGases", atomicNumber: 86, isRadioactive: true }),

    new AtomInfo({ name: "Francium", symbol: "Fr", type: "alkaliMetals", atomicNumber: 87, isRadioactive: true }),
    new AtomInfo({ name: "Radium", symbol: "Ra", type: "alkalineMetals", atomicNumber: 88, isRadioactive: true }),
    new AtomInfo({ name: "Actinium", symbol: "Ac", type: "actinoids", atomicNumber: 89, isRadioactive: true }),
    new AtomInfo({ name: "Thorium", symbol: "Th", type: "actinoids", atomicNumber: 90, isRadioactive: true }),
    new AtomInfo({ name: "Protactinium", symbol: "Pa", type: "actinoids", atomicNumber: 91, isRadioactive: true }),
    new AtomInfo({ name: "Uranium", symbol: "U", type: "actinoids", atomicNumber: 92, isRadioactive: true }),
    new AtomInfo({ name: "Neptunium", symbol: "Np", type: "actinoids", atomicNumber: 93, isRadioactive: true }),
    new AtomInfo({ name: "Plutonium", symbol: "Pu", type: "actinoids", atomicNumber: 94, isRadioactive: true }),
    new AtomInfo({ name: "Americium", symbol: "Am", type: "actinoids", atomicNumber: 95, isRadioactive: true }),
    new AtomInfo({ name: "Curium", symbol: "Cm", type: "actinoids", atomicNumber: 96, isRadioactive: true }),
    new AtomInfo({ name: "Berkelium", symbol: "Bk", type: "actinoids", atomicNumber: 97, isRadioactive: true }),
    new AtomInfo({ name: "Californium", symbol: "Cf", type: "actinoids", atomicNumber: 98, isRadioactive: true }),
    new AtomInfo({ name: "Einsteinium", symbol: "Es", type: "actinoids", atomicNumber: 99, isRadioactive: true }),
    new AtomInfo({ name: "Fermium", symbol: "Fm", type: "actinoids", atomicNumber: 100, isRadioactive: true }),
    new AtomInfo({ name: "Mendelevium", symbol: "Md", type: "actinoids", atomicNumber: 101, isRadioactive: true }),
    new AtomInfo({ name: "Nobelium", symbol: "No", type: "actinoids", atomicNumber: 102, isRadioactive: true }),
    new AtomInfo({ name: "Lawrencium", symbol: "Lr", type: "actinoids", atomicNumber: 103, isRadioactive: true }),
    new AtomInfo({ name: "Rutherfordium", symbol: "Rf", type: "transitionMetals", atomicNumber: 104, isRadioactive: true }),
    new AtomInfo({ name: "Dubnium", symbol: "Db", type: "transitionMetals", atomicNumber: 105, isRadioactive: true }),
    new AtomInfo({ name: "Seaborgium", symbol: "Sg", type: "transitionMetals", atomicNumber: 106, isRadioactive: true }),
    new AtomInfo({ name: "Bohrium", symbol: "Bh", type: "transitionMetals", atomicNumber: 107, isRadioactive: true }),
    new AtomInfo({ name: "Hassium", symbol: "Hs", type: "transitionMetals", atomicNumber: 108, isRadioactive: true }),
    new AtomInfo({ name: "Meitnerium", symbol: "Mt", type: "unknown", atomicNumber: 109, isRadioactive: true }),
    new AtomInfo({ name: "Darmastadtium", symbol: "Ds", type: "unknown", atomicNumber: 110, isRadioactive: true }),
    new AtomInfo({ name: "Roentgenium", symbol: "Rg", type: "unknown", atomicNumber: 111, isRadioactive: true }),
    new AtomInfo({ name: "Copernicium", symbol: "Cn", type: "otherMetals", atomicNumber: 112, isRadioactive: true }),
    new AtomInfo({ name: "Nihonium", symbol: "Nh", type: "unknown", atomicNumber: 113, isRadioactive: true }),
    new AtomInfo({ name: "Flerovium", symbol: "Fl", type: "unknown", atomicNumber: 114, isRadioactive: true }),
    new AtomInfo({ name: "Moscovium", symbol: "Mc", type: "unknown", atomicNumber: 115, isRadioactive: true }),
    new AtomInfo({ name: "Livermorium", symbol: "Lv", type: "unknown", atomicNumber: 116, isRadioactive: true }),
    new AtomInfo({ name: "Tennessine", symbol: "Ts", type: "unknown", atomicNumber: 117, isRadioactive: true }),
    new AtomInfo({ name: "Oganesson", symbol: "Og", type: "unknown", atomicNumber: 118, isRadioactive: true })
].forEach(atom => {
    if (atom.atomicNumber >= elements.length) {
        console.warn("Unknown element:", atom);
        return;
    }
    elements[atom.atomicNumber] = atom;
});



const TableMainSection = [
    [1, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, 2],
    [3, 4, null, null, null, null, null, null, null, null, null, null, 5, 6, 7, 8, 9, 10],
    [11, 12, null, null, null, null, null, null, null, null, null, null, 13, 14, 15, 16, 17, 18],
    [19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36],
    [37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51, 52, 53, 54],
    [55, 56, 57, 72, 73, 74, 75, 76, 77, 78, 79, 80, 81, 82, 83, 84, 85, 86],
    [87, 88, 89, 104, 105, 106, 107, 108, 109, 110, 111, 112, 113, 114, 115, 116, 117, 118],
];

const TableLanthanoidsActinoids = [
    [58, 59, 60, 61, 62, 63, 64, 65, 66, 67, 68, 69, 70, 71],
    [90, 91, 92, 93, 94, 95, 96, 97, 98, 99, 100, 101, 102, 103],
];

class PeriodicTable {
    constructor(clickCallback) {
        this.domElement = document.createElement('div');
        this.domElement.classList.add("periodic-table");

        let SectionData = {
            main: TableMainSection,
            lanthanoidsActinoids: TableLanthanoidsActinoids,
        };

        this._sections = {};

        for (let name in SectionData) {
            let ThisSectionData = SectionData[name];
            let section = new TableSection(ThisSectionData, clickCallback);
            this.domElement.appendChild(section.domElement);
            this._sections[name] = section;
        }

        this.setVisible(false);
    }

    setHighlighted(atomicNumber) {
        for (let name in this._sections) {
            let section = this._sections[name];
            section.setHighlighted(atomicNumber);
        }
    }

    setInteractive(interactive) {
        this.domElement.classList.toggle("interactive", interactive);
    }

    setVisible(enabled) {
        this.domElement.classList.toggle("hidden", !enabled);
    }
}

class TableSection {
    constructor(ElementTable, clickCallback) {
        let rowLen = ElementTable[0].length;

        this.domElement = document.createElement('div');
        this.domElement.classList.add("section");

        let tableCnt = document.createElement('div');
        tableCnt.classList.add("cnt");

        let table = document.createElement('table');

        this.domElement.appendChild(tableCnt);
        tableCnt.appendChild(table);

        this._elementCell = {};
        this._highlightedElement = null;

        for (let rowi = 0; rowi < ElementTable.length; rowi++) {
            let TableRow = ElementTable[rowi];

            let row = document.createElement('tr');
            table.appendChild(row);

            for (let coli = 0; coli < TableRow.length; coli++) {
                let cell = document.createElement('td');
                row.appendChild(cell);

                let atomicNumber = TableRow[coli];
                if (atomicNumber !== null) {
                    let atomInfo = elements[atomicNumber];

                    let info = document.createElement('div');
                    info.classList.add('info');
                    info.innerText = atomInfo.symbol;
                    info.style.background = GROUPS[atomInfo.type].color;

                    cell.appendChild(info);
                    this._elementCell[atomicNumber] = cell;

                    cell.addEventListener('click', () => {
                        clickCallback(atomicNumber, atomicNumber == 1 ? 0 : atomicNumber, atomicNumber);
                    });
                } else {
                    cell.style.border = 'none';
                }
            }
        }
    }

    setHighlighted(atomicNumber) {
        if (atomicNumber == this._highlightedElement) return;

        let oldCell = this._elementCell[this._highlightedElement];
        let newCell = this._elementCell[atomicNumber];

        if (oldCell) oldCell.classList.remove("highlighted");
        if (newCell) newCell.classList.add("highlighted");

        this._highlightedElement = atomicNumber;
    }
}

export default AtomUI;
export { AtomUI };
